import React from 'react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import PageHead from '@/components/PageHead';
import { Shield, Cookie, Database, Mail } from 'lucide-react';

export default function ConfidentialitePage() {
  return (
    <div data-testid="confidentialite-page">
      <PageHead title="Politique de confidentialité" description="Politique RGPD d'ALOFI SA. Vos droits sur les données collectées." />
      <section className="hero-bg noise-overlay relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px] bg-white/80">RGPD</Badge>
          <h1 className="mt-3 font-heading text-3xl sm:text-5xl font-semibold tracking-tight">Politique de confidentialité</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">ALOFI SA accorde une importance particulière à la protection de vos données personnelles, dans le respect du Règlement Général sur la Protection des Données (UE) 2016/679 et de la loi Informatique et Libertés.</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        <Card className="rounded-2xl border-border p-7">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center"><Shield className="w-5 h-5 text-[hsl(186_55%_38%)]" /></div>
            <h2 className="font-heading text-xl font-semibold">Responsable de traitement</h2>
          </div>
          <Separator className="my-4" />
          <p className="text-sm leading-relaxed">Le responsable de traitement des données collectées sur le site est <strong>ALOFI SA</strong>, SAS au capital de 64 000 €, dont le siège social est situé 92 Allée de la Picholine, 30320 Marguerittes, France, immatriculée au RCS de Nimes sous le n° 383 370 442. Pour toute demande relative à vos données personnelles, vous pouvez nous écrire à <a href="mailto:contact@alofisa.eu" className="underline">contact@alofisa.eu</a>.</p>
        </Card>

        <Card className="rounded-2xl border-border p-7">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center"><Database className="w-5 h-5 text-[hsl(186_55%_38%)]" /></div>
            <h2 className="font-heading text-xl font-semibold">Données collectées</h2>
          </div>
          <Separator className="my-4" />
          <p className="text-sm leading-relaxed">Nous collectons uniquement les données nécessaires aux traitements suivants :</p>
          <ul className="mt-3 list-disc pl-5 space-y-1.5 text-sm">
            <li><strong>Demande de devis</strong> : nom, société, email, téléphone, produit, quantité, message.</li>
            <li><strong>Formulaire de contact</strong> : nom, email, sujet, message.</li>
            <li><strong>Consultation du site</strong> : données techniques (IP anonymisée, navigateur, pages vues) à des fins statistiques.</li>
          </ul>
          <p className="text-sm leading-relaxed mt-3">Aucune donnée bancaire n’est collectée par le site (vente B2B sur devis uniquement).</p>
        </Card>

        <Card className="rounded-2xl border-border p-7">
          <h2 className="font-heading text-xl font-semibold">Finalités</h2>
          <Separator className="my-4" />
          <ul className="list-disc pl-5 space-y-1.5 text-sm">
            <li>Traiter vos demandes de devis et répondre à vos messages.</li>
            <li>Gérer la relation commerciale (B2B) et les commandes à la suite d’un devis accepté.</li>
            <li>Améliorer le contenu et l’ergonomie du site (statistiques anonymisées).</li>
            <li>Respecter nos obligations comptables et légales.</li>
          </ul>
        </Card>

        <Card className="rounded-2xl border-border p-7">
          <h2 className="font-heading text-xl font-semibold">Durée de conservation</h2>
          <Separator className="my-4" />
          <p className="text-sm leading-relaxed">Les données des prospects sont conservées <strong>3 ans</strong> après le dernier contact. Les données relatives à une relation commerciale sont conservées pendant la durée du contrat puis archivées selon les obligations comptables et fiscales en vigueur (jusqu’à 10 ans).</p>
        </Card>

        <Card className="rounded-2xl border-border p-7">
          <h2 className="font-heading text-xl font-semibold">Destinataires</h2>
          <Separator className="my-4" />
          <p className="text-sm leading-relaxed">Vos données sont strictement réservées à ALOFI SA et ne sont jamais cédées à des tiers à des fins commerciales. Elles peuvent être transmises à nos sous-traitants techniques (hébergeur IONOS, prestataire email) dans le strict cadre de l’exécution de leurs missions et sous obligation de confidentialité.</p>
        </Card>

        <Card className="rounded-2xl border-border p-7">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center"><Cookie className="w-5 h-5 text-[hsl(186_55%_38%)]" /></div>
            <h2 className="font-heading text-xl font-semibold">Cookies</h2>
          </div>
          <Separator className="my-4" />
          <p className="text-sm leading-relaxed">Le site utilise uniquement des cookies <strong>strictement nécessaires</strong> au fonctionnement (session, préférences). Aucun cookie publicitaire ou de traçage tiers n’est déposé sans votre consentement explicite.</p>
        </Card>

        <Card className="rounded-2xl border-border p-7">
          <h2 className="font-heading text-xl font-semibold">Vos droits</h2>
          <Separator className="my-4" />
          <p className="text-sm leading-relaxed">Conformément au RGPD, vous disposez d’un droit d’accès, de rectification, d’effacement, d’opposition, à la limitation du traitement, à la portabilité des données, ainsi que la possibilité d’introduire une réclamation auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noreferrer" className="underline">www.cnil.fr</a>).</p>
          <p className="text-sm leading-relaxed mt-3 flex items-center gap-2"><Mail className="w-4 h-4 text-[hsl(186_55%_38%)]" />Pour exercer vos droits, contactez-nous à <a href="mailto:contact@alofisa.eu" className="underline">contact@alofisa.eu</a>.</p>
        </Card>
      </section>
    </div>
  );
}
